import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Building2, ShieldCheck, BarChart3, Award, Users, BookOpen, CheckCircle, ArrowLeft, FileCheck, TrendingUp, Zap, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PartnerPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/learning/partner/stats`)
      .then(r => r.json())
      .then(d => { if (d.success) setStats(d.platform_stats); })
      .catch(console.error);
  }, []);

  const benefits = [
    { icon: ShieldCheck, title: 'Digitale Schulungsnachweise', desc: 'Zertifikate mit QR-Code-Verifizierung. Jederzeit online prüfbar für Kammern und Aufsichtsbehörden.' },
    { icon: FileCheck, title: 'Compliance-Monitoring', desc: 'Echtzeit-Übersicht über den Schulungsstand aller Betriebe. Pflichtschulungen (DGUV V1, VDE 0105) automatisch überwacht.' },
    { icon: BarChart3, title: 'Statistiken & Reports', desc: 'Detaillierte Auswertungen über Schulungsaktivitäten, Bestehensquoten und Weiterbildungstrends in der Region.' },
    { icon: Users, title: 'Betriebsübergreifend', desc: 'Einheitlicher Standard für alle Elektrobetriebe. Vergleichbare Qualifikationsnachweise in der gesamten Kammer.' },
    { icon: BookOpen, title: 'Normenkonforme Inhalte', desc: 'Alle Schulungen basieren auf aktuellen DGUV/VDE/DIN-Normen. Regelmäßig aktualisiert bei Normenänderungen.' },
    { icon: Zap, title: 'API-Schnittstelle', desc: 'Technische Anbindung an bestehende Kammer-Systeme über REST-API. Automatischer Datenabgleich möglich.' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black">
      <header className="bg-black/60 border-b border-yellow-500/20 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white" data-testid="partner-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-yellow-500" />
              <h1 className="text-lg font-bold text-white">Partnerschaft</h1>
            </div>
          </div>
          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 border">Handwerkskammer-Programm</Badge>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-5xl" data-testid="partner-page">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-4 py-1.5 mb-6">
            <Building2 className="w-4 h-4 text-yellow-500" />
            <span className="text-yellow-500 text-sm font-medium">Kooperationsangebot</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Digitale Weiterbildung<br />
            <span className="text-yellow-500">für Ihre Kammer-Betriebe</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            SmartVolt Academy bietet Handwerkskammern eine schlüsselfertige E-Learning-Plattform
            für normenkonforme Pflichtschulungen im Elektrohandwerk.
          </p>
        </div>

        {/* Platform Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
            {[
              { val: stats.registered_companies, label: 'Betriebe' },
              { val: stats.active_learners, label: 'Lernende' },
              { val: stats.certificates_issued, label: 'Zertifikate' },
              { val: stats.available_courses, label: 'Kurse' },
              { val: stats.total_lessons, label: 'Lektionen' }
            ].map((s, i) => (
              <Card key={i} className="bg-gray-900/30 border-gray-800 text-center">
                <CardContent className="pt-6">
                  <p className="text-3xl font-bold text-yellow-500">{s.val}</p>
                  <p className="text-sm text-gray-400 mt-1">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Benefits Grid */}
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Vorteile der Partnerschaft</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefits.map((b, i) => (
            <Card key={i} className="bg-gray-900/30 border-gray-800 hover:border-yellow-500/30 transition-all">
              <CardContent className="pt-6">
                <b.icon className="w-8 h-8 text-yellow-500 mb-3" />
                <h3 className="text-white font-bold mb-2">{b.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Compliance Standards */}
        <Card className="bg-gray-900/30 border-gray-800 mb-16">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" /> Abgedeckte Normen & Vorschriften
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                'DGUV Vorschrift 1', 'VDE 0105-100', 'TRBS 2121', 'ArbStättV',
                'VDE AR-N 4105', 'DIN EN 361', 'GoBD', 'BGB/VOB Werkvertragsrecht',
                'Bildschirmarbeitsverordnung'
              ].map((norm, i) => (
                <div key={i} className="flex items-center gap-2 bg-black/30 p-3 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  <span className="text-sm text-gray-300">{norm}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Kooperationsmodelle */}
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Kooperationsmodelle</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <Card className="bg-gray-900/30 border-gray-800">
            <CardHeader>
              <Badge className="bg-gray-700 text-gray-300 w-fit mb-2">Standard</Badge>
              <CardTitle className="text-white">Empfehlung</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm mb-4">Die Kammer empfiehlt SmartVolt Academy als bevorzugte E-Learning-Plattform für Elektrobetriebe.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Nennung im Kammer-Newsletter</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Link auf Kammer-Website</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Sonderkonditionen für Mitglieder</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/30 border-yellow-500/50 border-2 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-yellow-500 text-black">Empfohlen</Badge>
            </div>
            <CardHeader>
              <Badge className="bg-yellow-500/20 text-yellow-500 w-fit mb-2 mt-2">Premium</Badge>
              <CardTitle className="text-white">Integration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm mb-4">Tiefe Integration in die Kammer-Infrastruktur mit Co-Branding und API-Anbindung.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-yellow-500" /> Kammer-Logo auf Zertifikaten</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-yellow-500" /> API-Schnittstelle</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-yellow-500" /> Compliance-Dashboard</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-yellow-500" /> Dedizierter Ansprechpartner</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/30 border-gray-800">
            <CardHeader>
              <Badge className="bg-purple-500/20 text-purple-400 w-fit mb-2">Enterprise</Badge>
              <CardTitle className="text-white">White-Label</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm mb-4">Vollständig angepasste Plattform im Kammer-Design mit eigenem Branding.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Eigenes Branding & Design</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Eigene Domain</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Individuelle Kurse</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-purple-400" /> Vollständige Kontrolle</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-yellow-500/10 to-yellow-600/10 border-yellow-500/30 text-center">
          <CardContent className="py-12">
            <Building2 className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Interesse an einer Partnerschaft?</h2>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">
              Kontaktieren Sie uns für ein unverbindliches Gespräch über die Möglichkeiten einer Kooperation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:elektrotechnik.seidel@outlook.com?subject=Partnerschaft SmartVolt Academy"
                className="inline-flex items-center justify-center gap-2 bg-yellow-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition"
                data-testid="partner-contact-btn">
                Kontakt aufnehmen <ArrowRight className="w-4 h-4" />
              </a>
              <Button variant="outline" onClick={() => navigate('/zertifikat-pruefen')}
                className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10" data-testid="verify-demo-btn">
                <ShieldCheck className="w-4 h-4 mr-2" /> Zertifikat-Verifizierung testen
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      <footer className="border-t border-gray-800 mt-16 py-8 text-center text-gray-500 text-sm">
        SmartVolt Academy - Digitale Weiterbildung für das Elektrohandwerk
      </footer>
    </div>
  );
};

export default PartnerPage;
